import { Button, PopoverProps, Tag } from 'antd';
import classNames from 'classnames';
import { keys } from 'lodash';
import React, { isValidElement, useMemo, useRef } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ErrorBoundary from '@/components/error-boundary';

import { DataViewer } from '@/utils/helpers/common';
import { ISelectOption } from '@/utils/interfaces/form';

import BaseAction, { SecondaryActionDefault } from './BaseAction';
import ErrorFallback from './ErrorFallback';
import SearchBox from './SearchBox';

import './Styles.scss';

export type RefActionControl = {
  toggle: (value: boolean) => void;
};
type SearchBoxSection = {
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  name?: string;
} | null;

type PrimaryActSection = {
  label: string;
  popoverContent: React.ReactNode | React.ReactElement<any>;
  disabled?: boolean;
  name?: string;
  totalFiltered?: number;
  refControl?: React.MutableRefObject<RefActionControl>;
  getPopupContainer?: PopoverProps['getPopupContainer'];
} | null;

type ThirdActSection = {
  label: string;
  popoverContent: React.ReactNode | React.ReactElement<any>;
  disabled?: boolean;
  refControl?: React.MutableRefObject<RefActionControl>;
  getPopupContainer?: PopoverProps['getPopupContainer'];
  onOpenChange?: (open: boolean) => void;
  showDropdownIcon?: boolean;
} | null;

type SecondaryActSection = {
  label: string;
  options: ISelectOption[];
  disabled?: boolean;
  name?: string;
  popoverContent?: React.ReactNode | React.ReactElement<any>;
  refControl?: React.MutableRefObject<RefActionControl>;
  getPopupContainer?: PopoverProps['getPopupContainer'];
} | null;

type TagSection = {
  renderTag?: (fieldKey: string, fieldValue: any) => React.ReactNode | React.ReactElement<any>;
  onClear?: (
    field: { [key: string]: any } | null,
    meta?: {
      key: string;
      value: any;
      removedValue?: any;
      currentValue?: any;
      currentFields?: { [key: string]: any };
    }
  ) => void;
} | null;

export interface IFormBaseFilterProps {
  searchBox?: SearchBoxSection | React.ReactNode | React.ReactElement<any>;
  primaryAction?: PrimaryActSection | React.ReactNode | React.ReactElement<any>;
  primaryActionFieldName?: string;
  secondaryAction?: SecondaryActSection | React.ReactNode | React.ReactElement<any>;
  thirdAction?: ThirdActSection | React.ReactNode | React.ReactElement<any>;
  tagSection?: TagSection | React.ReactNode | React.ReactElement<any>;
  defaultOrderTagSection?: string[];
  onSubmit?: (values: FieldValues, isChangeView?: boolean) => void;
  loading?: boolean;
  changeAction?: boolean;
}

const Filter = (props: IFormBaseFilterProps) => {
  const { t } = useTranslation();
  const { searchBox, primaryAction, primaryActionFieldName, secondaryAction, thirdAction, tagSection, onSubmit, loading, changeAction } = props;
  const methods = useFormContext();
  const primaryRef = useRef<any>(null);
  const primaryFieldName =
    primaryActionFieldName ?? (primaryAction !== null && !isValidElement(primaryAction) ? (primaryAction as PrimaryActSection)?.name ?? '' : '');
  const watchPrimaryFields = primaryFieldName ? methods.watch(primaryFieldName) ?? {} : {};

  const handleFormSubmit = (values: FieldValues) => {
    onSubmit?.(values);
  };

  const defaultOrderDict = useMemo(() => {
    return (props.defaultOrderTagSection ?? []).reduce((orderDict: Record<string, number>, inputName: string, index) => {
      orderDict[inputName] = index;
      return orderDict;
    }, {});
  }, [props.defaultOrderTagSection]);

  const generateSearchBoxSection = () => {
    if (searchBox === null) return <></>;
    if (isValidElement(searchBox)) return <div className='search-box-wrapper'>{searchBox}</div>;
    const { name = 'search' } = (searchBox as SearchBoxSection) ?? {};
    return (
      <div className='search-box-wrapper'>
        <SearchBox
          control={methods.control}
          name={name}
          onSubmit={methods.handleSubmit((values) => {
            onSubmit?.(values);
          })}
          maxLength={128}
          {...(searchBox as SearchBoxSection)}
        />
      </div>
    );
  };

  const calculateTotalPrimaryFilters = (defaultVal?: number): number | undefined => {
    if (typeof defaultVal === 'number' && defaultVal === 0) return undefined;
    if (typeof defaultVal === 'number') return defaultVal;
    if (tagSection === null || !primaryFieldName) {
      console.warn('%c Calculate must required primaryAction as object or primaryActionFieldName and tagSection', 'font-size:14px;color:red');
      return undefined;
    }

    const fieldKeyHasValue = keys(watchPrimaryFields).filter((key) => {
      const value = watchPrimaryFields[key];

      if (Array.isArray(value)) return value.length > 0;

      return !DataViewer.isEmpty(value);
    });

    return fieldKeyHasValue.length > 0 ? fieldKeyHasValue.length : undefined;
  };

  const generateActionGroup = () => {
    if (primaryAction === null && secondaryAction === null && thirdAction === null) return <></>;
    if (isValidElement(primaryAction) && !changeAction) {
      return <div className='flex justify-center items-center'>{primaryAction}</div>;
    }
    const { label: primaryLabel = 'button:filter', popoverContent: primaryPopover, totalFiltered } = (primaryAction as PrimaryActSection) ?? {};
    const {
      label: secondaryLabel = 'project:second_filter:my_project',
      popoverContent: secondaryPopover = (
        <SecondaryActionDefault
          name={(secondaryAction as SecondaryActSection)?.name ?? 'secondaryFilters'}
          options={(secondaryAction as SecondaryActSection)?.options ?? []}
          {...(secondaryAction as SecondaryActSection)}
          onSubmit={methods.handleSubmit((values) => onSubmit?.(values, true))}
        />
      )
    } = (secondaryAction as PrimaryActSection) ?? {};
    const { label: thirdLabel = '', popoverContent: thirdPopover } = (thirdAction as ThirdActSection) ?? {};
    return (
      <div className='flex justify-center items-center gap-[16px]'>
        {secondaryAction !== null && (
          <div>
            {isValidElement(secondaryAction) ? (
              secondaryAction
            ) : (
              <BaseAction label={secondaryLabel} popoverContent={secondaryPopover} {...(secondaryAction as SecondaryActSection)} />
            )}
          </div>
        )}
        {primaryAction !== null && (
          <div ref={primaryRef}>
            {isValidElement(primaryAction) ? (
              primaryAction
            ) : (
              <BaseAction
                label={primaryLabel}
                popoverContent={primaryPopover}
                {...(primaryAction as PrimaryActSection)}
                totalFiltered={calculateTotalPrimaryFilters(totalFiltered)}
              />
            )}
          </div>
        )}
        {thirdAction !== null && (
          <div>
            {isValidElement(thirdAction) ? (
              thirdAction
            ) : (
              <BaseAction label={thirdLabel} popoverContent={thirdPopover} {...(thirdAction as ThirdActSection)} />
            )}
          </div>
        )}
      </div>
    );
  };

  const submitForm = () => {
    methods.handleSubmit(handleFormSubmit)();
  };

  const generateFilterTags = () => {
    if (tagSection === null) return <></>;
    if (isValidElement(tagSection)) {
      return <div className='justify-center items-center basis-full'>{tagSection}</div>;
    }

    if (primaryAction === null || !primaryFieldName) {
      console.warn('%c Generic tag render required primaryAction or primaryActionFieldName and primaryAction.name', 'font-size:14px;color:red');
      return <></>;
    }

    const renderTag = (tagSection as TagSection)?.renderTag;
    const clearTag = (tagSection as TagSection)?.onClear;

    const getItemValue = (item: any) => item?.value ?? item;

    const handleRemoveArrayItem = (key: string, value: any[], item: any) => {
      const newData = value.filter((v) => getItemValue(v) !== getItemValue(item));

      if (clearTag) {
        clearTag(
          {
            [key]: newData.length ? newData : undefined
          },
          {
            key,
            value: newData.length ? newData : undefined,
            removedValue: item,
            currentValue: value,
            currentFields: watchPrimaryFields
          }
        );

        return;
      }

      methods.setValue(primaryFieldName, {
        ...watchPrimaryFields,
        [key]: newData.length ? newData : undefined
      });

      submitForm();
    };

    const handleRemoveSingle = (key: string) => {
      if (clearTag) {
        clearTag(
          {
            [key]: undefined
          },
          {
            key,
            value: undefined,
            currentValue: watchPrimaryFields[key],
            currentFields: watchPrimaryFields
          }
        );

        return;
      }

      methods.setValue(primaryFieldName, {
        ...watchPrimaryFields,
        [key]: undefined
      });

      submitForm();
    };

    const renderArrayTag = (key: string, item: any, value: any[]) => {
      const tagKey = getItemValue(item);

      return (
        <Tag
          key={`${key}-${tagKey}`}
          closable
          className='bg-gray2 body-400 tag-item flex items-center'
          onClose={(e) => {
            e.preventDefault();
            handleRemoveArrayItem(key, value, item);
          }}
        >
          {typeof renderTag === 'function' ? renderTag(key, item) : item}
        </Tag>
      );
    };

    const renderSingleTag = (key: string, value: any) => {
      return (
        <Tag
          key={key}
          closable
          className='bg-gray2 body-400 tag-item flex items-center'
          onClose={(e) => {
            e.preventDefault();
            handleRemoveSingle(key);
          }}
        >
          {typeof renderTag === 'function' ? renderTag(key, value) : value}
        </Tag>
      );
    };
    const orderedKeys = keys(watchPrimaryFields).sort((a, b) => {
      const indexA = defaultOrderDict[a] ?? 0;
      const indexB = defaultOrderDict[b] ?? 0;
      return indexA - indexB;
    });

    const fieldKeyHasValue = orderedKeys
      .filter((key) => {
        return typeof renderTag !== 'function' || renderTag(key, watchPrimaryFields[key]) !== null;
      })
      .filter((key) => (Array.isArray(watchPrimaryFields[key]) ? watchPrimaryFields[key].length : !DataViewer.isEmpty(watchPrimaryFields[key])));

    if (loading) return <></>;

    return (
      <div className={classNames('flex flex-wrap gap-2 justify-start items-center basis-full', fieldKeyHasValue?.length && 'pt-[10px]')}>
        {fieldKeyHasValue.flatMap((key) => {
          const value = watchPrimaryFields[key];

          if (Array.isArray(value)) {
            return value.map((item) => renderArrayTag(key, item, value));
          }

          return renderSingleTag(key, value);
        })}

        {fieldKeyHasValue.length ? (
          <Button
            size='small'
            className='tag-btn__clear-all'
            onClick={(e) => {
              e.preventDefault();

              if (clearTag) {
                clearTag(null);
                return;
              }

              let resetValues;
              if (Array.isArray(watchPrimaryFields)) {
                resetValues = [];
              } else if (typeof watchPrimaryFields === 'object') {
                resetValues = {};
              }

              methods.setValue(primaryFieldName, resetValues);
              submitForm();
            }}
          >
            {t('button:clear_filter')}
          </Button>
        ) : null}
      </div>
    );
  };

  return (
    <div className='base-filter-wrapper flex justify-between flex-wrap'>
      {generateSearchBoxSection()}
      {generateActionGroup()}
      {generateFilterTags()}
    </div>
  );
};

/**
 * This component required react-hook-form and wrapped by FormProvider.
 * @version 0.1.1
 *
 * @param searchBox - U can choose with my pattern or a React Component
 * @param primaryAction - U can choose with my pattern or a React Component
 * @param secondaryAction - U can choose with my pattern or a React Component
 * @param tagSection - U can choose with my pattern or a React Component
 * @param onSubmit - callback when submit filter. U can handle it with useForm().handleSubmit at component wrapped this Filter.
 *
 * @example
 * export default function App() {
 * const methods = useForm()
 * const applyFilter = (data) => console.log(data)
 *
 * const subMethods = useForm({defaultValues:{text1: 'text1', text2: 'text2'}});
 * const submitPrimaryFilters = (values: FieldValues) => {
 *   filterForm.setValue('primaryFilters', { ...values} );
 *   filterForm.handleSubmit(applyFilter)();
 * };
 * return <FormProvider {...methods}>
 *    <FormBaseFilter
 *        onSubmit={applyFilter}
 *        searchBox={{ placeholder: 'search anything', name:'searchValue' }}
 *        primaryAction={{
 *          label: 'button:filter',
 *          name: 'primaryFilters',
 *          popoverContent: <FormProvider {...subMethods}>
 *              <div className='p-3'>
 *                  <input
 *                      className='my-3'
 *                      type='text'
 *                      {...subMethods.register('text1')}
 *                  />
 *                  <input
 *                      className='my-3'
 *                      type='text'
 *                      {...subMethods.register('text2')}
 *                  />
 *              </div>
 *              <BaseButton
 *                  className='mx-auto cursor-pointer'
 *                  onClick={subMethods.handleSubmit(submitPrimaryFilters)}>
 *                    submit
 *               </BaseButton>
 *            </FormProvider>,
 *            totalFiltered: 2
 *        }}
 *        secondaryAction={{
 *            label: 'project:second_filter:my_project',
 *            name: 'secondaryFilters',
 *            options: [
 *                { label: 'option1', value: 1 },
 *                { label: 'option2', value: 2 }
 *            ]
 *         }}
 *    />
 *  </FormProvider>;
 * };
 */
const FormBaseFilter = (props: IFormBaseFilterProps) => {
  const {
    defaultOrderTagSection = [
      'category',
      'countryIds',
      'formId',
      'categoryId',
      'patternId',
      'picIds',
      'companyId',
      'location',
      'process',
      'status',
      'departureFrom',
      'departureTo',
      'createDateFrom',
      'createDateTo',
      'updatedDateFrom',
      'updatedDateTo'
    ],
    ...rest
  } = props;
  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <Filter defaultOrderTagSection={defaultOrderTagSection} {...rest} />
    </ErrorBoundary>
  );
};

export default FormBaseFilter;
